import React, { useCallback, useState } from 'react';
import { Box, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { Close } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation, getSessionStorage } from '@shared/core';
import { TravelCurrencySelector } from '@shared/components/TravelCurrencySelector';
import { useTravelWhiteLabelCurrency } from '@shared/hooks/useTravelWhiteLabelCurrency';
import type { TopBarConfig } from '@shared/types/Accommodations.types';

const NOTICE_SEEN_KEY = 'joy:cashFundCurrencyNoticeSeen';

type CurrencyPickerWithFundNoticeProps = Readonly<{
  notice: NonNullable<TopBarConfig['cashFundNotice']>;
  /** `pill` draws the toolbar pill. See `TravelCurrencySelector`. */
  buttonAppearance?: 'default' | 'pill';
}>;

/**
 * The standard currency picker plus a dismissible note about the honeymoon fund. The picker and its
 * dialog are untouched — the couple's currency choice always wins; this only explains the effect.
 */
export const CurrencyPickerWithFundNotice = ({ notice, buttonAppearance }: CurrencyPickerWithFundNoticeProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  const { currency: pageCurrency } = useTravelWhiteLabelCurrency();

  const storage = getSessionStorage();
  const [isDismissed, setIsDismissed] = useState(() => storage.getItem(NOTICE_SEEN_KEY) === 'true');

  const dismiss = useCallback(() => {
    setIsDismissed(true);
    storage.setItem(NOTICE_SEEN_KEY, 'true');
  }, [storage]);

  // Priced in something the fund cannot pay, so the copy matches the checkout banner that says so.
  const isCurrencyMismatch = notice.fundCurrency !== pageCurrency;

  return (
    <TooltipV2
      isOpen={!isDismissed}
      triggerType="none"
      // `bottomEnd` because the picker sits in the far-right corner: centred, the tooltip overflows
      // the viewport and gets squeezed to a one-word column. Joykit placements are camelCase.
      placement="bottomEnd"
      content={
        <Flex
          gap={2}
          alignItems="flex-start"
          width="max-content"
          maxWidth={pxToRem(260)}
        >
          <Box>
            {isCurrencyMismatch ? (
              <TextV2
                color="mono4"
                typographyVariant="label2"
              >
                {tq`Your honeymoon fund has ${notice.balanceDisplay} available — switch to ${notice.fundCurrency} to use it on this stay.`}
              </TextV2>
            ) : (
              <>
                <TextV2
                  color="mono4"
                  fontWeight={600}
                  typographyVariant="label2"
                >
                  {tq`Your honeymoon fund uses this currency.`}
                </TextV2>
                <TextV2
                  color="mono4"
                  typographyVariant="label2"
                >
                  {tq`Select currency to browse hotels in another currency without your fund.`}
                </TextV2>
              </>
            )}
          </Box>
          <Box
            as="button"
            padding={0}
            color="mono4"
            type="button"
            border="none"
            display="flex"
            flexShrink={0}
            cursor="pointer"
            alignItems="center"
            onClick={dismiss}
            justifyContent="center"
            backgroundColor="transparent"
            aria-label={tq`Close tooltip`}
          >
            <Close size="sm" />
          </Box>
        </Flex>
      }
    >
      <Box>
        <TravelCurrencySelector
          isEnabled
          buttonAppearance={buttonAppearance}
        />
      </Box>
    </TooltipV2>
  );
};
